import { useState, type FormEvent, type JSX } from 'react'
import { AlertTriangle, X } from 'lucide-react'

const R = 'rounded-[3px]'
const BTN_DANGER = `flex h-9 items-center gap-1.5 ${R} border border-red-200 bg-white px-4 text-[13px] font-semibold text-red-600 transition-colors hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-40`
const BTN_SECONDARY = `flex h-9 items-center gap-1.5 ${R} border border-slate-200 bg-white px-4 text-[13px] font-semibold text-slate-600 transition-colors hover:border-slate-300 hover:text-slate-900 disabled:cursor-not-allowed disabled:opacity-40`
const INPUT = `w-full ${R} border border-slate-200 bg-white px-3 py-2 text-[13px] text-slate-700 outline-none transition-colors focus:border-[#3366FF] focus:ring-2 focus:ring-[#3366FF]/10`

interface MfaDisableDialogProps {
  onDisable: (password: string) => void
  onCancel: () => void
  loading: boolean
  error: string | null
}

export function MfaDisableDialog({
  onDisable,
  onCancel,
  loading,
  error,
}: MfaDisableDialogProps): JSX.Element {
  const [password, setPassword] = useState('')

  function handleSubmit(e: FormEvent): void {
    e.preventDefault()
    if (password) {
      onDisable(password)
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Disable MFA"
    >
      <div className={`w-full max-w-md border border-slate-200 bg-white p-6 shadow-xl ${R}`}>
        <div className="mb-4 flex items-start justify-between">
          <div className="flex items-center gap-3">
            <span
              className={`flex h-10 w-10 shrink-0 items-center justify-center ${R} bg-red-50 text-red-600`}
            >
              <AlertTriangle className="h-5 w-5" />
            </span>
            <div>
              <h3 className="text-[17px] font-bold leading-tight tracking-tight text-[#0f172a]">
                Disable MFA
              </h3>
              <p className="mt-0.5 text-[12.5px] text-slate-500">
                Your account will no longer be protected by two-factor authentication.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onCancel}
            className={`${R} p-1.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700`}
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {error && <p className="mb-3 text-sm text-red-600">{error}</p>}

        <form onSubmit={handleSubmit} className="space-y-3">
          <input
            type="password"
            placeholder="Current password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className={INPUT}
          />
          <div className="flex flex-wrap gap-2">
            <button
              type="submit"
              className={BTN_DANGER}
              disabled={!password || loading}
            >
              <AlertTriangle className="h-4 w-4" />
              Disable MFA
            </button>
            <button type="button" className={BTN_SECONDARY} onClick={onCancel}>
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
